import { useCallback, useMemo, useState } from "react";
import type { DataTablePageEvent, DataTableSortEvent } from "primereact/datatable";

import { TABLE_DEFAULT_ROWS, TABLE_ROWS_OPTIONS } from "./constants";
import type { TableSort } from "./table-sort";

export interface UseTableStateOptions<F extends string> {
  /** Linhas por página inicial. Padrão: `TABLE_DEFAULT_ROWS` (20). */
  defaultRows?: number;
  /** Opções do seletor de linhas por página. Padrão: `TABLE_ROWS_OPTIONS` (`[10, 20, 50]`). */
  rowsOptions?: readonly number[];
  /** Ordenação inicial. Padrão: sem ordem. */
  defaultSort?: TableSort<F> | null;
  /** Guarda de tipo do campo ordenável; `onSort` ignora o que não passar. */
  isSortField: (value: unknown) => value is F;
  /** Total de registros; se ficar abaixo de `first`, a página volta para a primeira. */
  total?: number;
  /**
   * Critério da consulta (busca, filtros, ordenação) em texto estável; quando muda, a página volta para a primeira.
   * Útil quando a ordenação mora fora do hook (ex.: compartilhada com outra visão).
   */
  resetKey?: string;
}

export interface UseTableStateResult<F extends string> {
  first: number;
  rows: number;
  /** Página atual, base 0. */
  page: number;
  rowsOptions: number[];
  sort: TableSort<F> | null;
  /** `sort` no formato do `DataTable` do Prime. */
  sortField: F | undefined;
  sortOrder: 1 | -1 | 0;
  /** Aceita o evento do `DataTable`/`Paginator` do Prime e o do `TablePaginator` (só `first` e `rows` importam). */
  onPage: (event: Pick<DataTablePageEvent, "first" | "rows">) => void;
  onSort: (event: DataTableSortEvent) => void;
  /** Muda as linhas por página e volta para a primeira página. */
  setRows: (rows: number) => void;
  /** Volta para a primeira página (ex.: quando o critério de busca ou filtro muda). */
  resetPage: () => void;
}

/**
 * Paginação e ordenação de um `DataTable` `lazy`, no formato canônico `TableSort`.
 * O ciclo sem ordem → asc → desc → sem ordem vem do `removableSort` do Prime; o hook guarda o resultado.
 */
export function useTableState<F extends string>({
  defaultRows = TABLE_DEFAULT_ROWS,
  rowsOptions = TABLE_ROWS_OPTIONS,
  defaultSort = null,
  isSortField,
  total,
  resetKey,
}: UseTableStateOptions<F>): UseTableStateResult<F> {
  const rowsOptionsList = useMemo(() => [...rowsOptions], [rowsOptions]);
  const [first, setFirst] = useState(0);
  const [rows, setRowsState] = useState(defaultRows);
  const [sort, setSort] = useState<TableSort<F> | null>(defaultSort);

  // Critério novo recomeça da página 1. Derivado no render (e não em efeito) para a consulta já sair com `page: 1`.
  const [lastResetKey, setLastResetKey] = useState(resetKey);
  if (resetKey !== lastResetKey) {
    setLastResetKey(resetKey);
    setFirst(0);
  }

  // Total encolheu (ex.: excluiu o último item da última página): a página atual ficou vazia.
  if (total !== undefined && first > 0 && first >= total) setFirst(0);

  const onPage = useCallback((event: Pick<DataTablePageEvent, "first" | "rows">) => {
    setFirst(event.first);
    setRowsState(event.rows);
  }, []);

  const onSort = useCallback(
    (event: DataTableSortEvent) => {
      if (!event.sortOrder) {
        setSort(null);
        setFirst(0);
        return;
      }
      if (!isSortField(event.sortField)) return;
      setSort({ field: event.sortField, direction: event.sortOrder > 0 ? "asc" : "desc" });
      setFirst(0);
    },
    [isSortField],
  );

  const setRows = useCallback((next: number) => {
    setRowsState(next);
    setFirst(0);
  }, []);

  const resetPage = useCallback(() => setFirst(0), []);

  return useMemo(
    () => ({
      first,
      rows,
      page: Math.floor(first / rows),
      rowsOptions: rowsOptionsList,
      sort,
      sortField: sort?.field,
      sortOrder: sort ? (sort.direction === "asc" ? (1 as const) : (-1 as const)) : (0 as const),
      onPage,
      onSort,
      setRows,
      resetPage,
    }),
    [first, rows, rowsOptionsList, sort, onPage, onSort, setRows, resetPage],
  );
}
